/**
 * Fungi Handler
 *
 * This Handler applies special case rules to Fungi interactions!
 *
 * When a Fungi King is captured by any animal - it controls that animal for one turn before the capture takes full effect!
 * This means that if the Fungi king can race (as the enemy piece) or capture enemy king on that turn - it will win the game!
 *
 * This Handler must ensure that it checks win conditions for Fungi kings ZOMBIE RACE and ZOMBIE KINGSLAYER before being lured in to the trap of a "free" King capture!
 */

import { MCTSMoveGenerationService } from "../../../../utils/MCTSMoveGenerationService";
import {
  GameState,
  Position,
  PieceType,
  IPiece,
  IMove,
  MoveType,
} from "../../../../../types";
import {
  MCTSUtils_v2,
  cloneFullGameState,
  getPieceFromPosition,
  applyMoveWithSnakeRespawn,
  findKing,
} from "../../../../utils/MCTSUtils";
import { DecisionContext, MoveInfo } from "../../../interfaces/DecisionPriority";

export interface FungiDecision {
  allowFungiKingCapture: boolean;
  canZombieRace: boolean;
  canZombieKingslayer: boolean;
  reasoning: string;
}

export class FungiHandler {
  private static instance: FungiHandler;

  public static getInstance(): FungiHandler {
    if (!FungiHandler.instance) {
      FungiHandler.instance = new FungiHandler();
    }
    return FungiHandler.instance;
  }

  /**
   * Main evaluation method for Fungi king capture decisions
   */
  public evaluateFungiKingCapture(
    moveInfo: MoveInfo,
    gameState: GameState
  ): FungiDecision {
    // Verify this is actually a Fungi king capture
    const targetPiece = getPieceFromPosition(gameState, moveInfo.move.position);
    if (
      !targetPiece ||
      targetPiece.type !== PieceType.Fungi ||
      !targetPiece.isKing
    ) {
      return {
        allowFungiKingCapture: true,
        canZombieRace: false,
        canZombieKingslayer: false,
        reasoning: "Not a Fungi king capture",
      };
    }

    const capturingPiece = getPieceFromPosition(
      gameState,
      moveInfo.fromPosition
    );
    if (!capturingPiece) {
      return {
        allowFungiKingCapture: true,
        canZombieRace: false,
        canZombieKingslayer: false,
        reasoning: "No capturing piece found",
      };
    }

    // Simulate the zombification scenario
    const zombieThreats = this.simulateZombificationThreats(
      moveInfo,
      gameState,
      capturingPiece,
      targetPiece
    );

    const allowCapture =
      !zombieThreats.canZombieRace && !zombieThreats.canZombieKingslayer;

    return {
      allowFungiKingCapture: allowCapture,
      canZombieRace: zombieThreats.canZombieRace,
      canZombieKingslayer: zombieThreats.canZombieKingslayer,
      reasoning: zombieThreats.reasoning,
    };
  }

  public evaluateFungiBullyingMove(
    moveInfo: MoveInfo,
    gameState: GameState
  ): { allowMove: boolean; reasoning: string; jukeScore: number } {
    const targetPos = moveInfo.move.position;
    const fungiPiece = getPieceFromPosition(gameState, moveInfo.fromPosition);
    if (!fungiPiece || fungiPiece.type !== PieceType.Fungi) {
      return { allowMove: false, reasoning: "Not a Fungi move", jukeScore: 0 };
    }

    // Check adjacent diagonals for Octopus
    const diagonals = [
      { row: targetPos.row - 1, col: targetPos.col - 1 },
      { row: targetPos.row - 1, col: targetPos.col + 1 },
      { row: targetPos.row + 1, col: targetPos.col - 1 },
      { row: targetPos.row + 1, col: targetPos.col + 1 }
    ];

    let threatenedOctopus: Position | null = null;
    for (const diag of diagonals) {
      if (diag.row < 0 || diag.row >= 8 || diag.col < 0 || diag.col >= 5) continue;
      const piece = getPieceFromPosition(gameState, diag);
      if (piece && piece.type === PieceType.Octopus && piece.isLight !== fungiPiece.isLight) {
        threatenedOctopus = diag;
        break;
      }
    }

    if (!threatenedOctopus) {
      return { allowMove: false, reasoning: "No Octopus threatened diagonally", jukeScore: 0 };
    }

    // Simulate the move
    const testState = cloneFullGameState(gameState);
    applyMoveWithSnakeRespawn(testState, moveInfo.fromPosition, moveInfo.move, fungiPiece);

    // Check if Octopus must retreat or be captured next turn
    testState.isLightTurn = !gameState.isLightTurn; // Enemy's turn
    const octopusMoves = MCTSMoveGenerationService.getInstance().getAllMoves(testState).filter(m => 
      m.fromPosition.row === threatenedOctopus!.row && m.fromPosition.col === threatenedOctopus!.col
    );

    const canCaptureFungi = octopusMoves.some(m => 
      m.move.position.row === targetPos.row && m.move.position.col === targetPos.col && m.move.moveType === MoveType.capture
    );

    if (!canCaptureFungi) {
      // Octopus cannot capture Fungi - it's bullied/forced to retreat
      // Check if this gains tempo for our king advance
      testState.isLightTurn = gameState.isLightTurn; // Back to our turn
      const ourKingMoves = MCTSMoveGenerationService.getInstance().getAllMoves(testState).filter(m => {
        const piece = getPieceFromPosition(testState, m.fromPosition);
        return piece?.isKing;
      });

      const advancesRace = ourKingMoves.some(m => Math.abs(m.move.position.row - (gameState.isLightTurn ? 7 : 0)) < Math.abs(moveInfo.fromPosition.row - (gameState.isLightTurn ? 7 : 0)));

      if (advancesRace) {
        return { allowMove: true, reasoning: "Bullying move forces Octopus retreat and gains king advance tempo", jukeScore: 0.9 };
      }
    }

    return { allowMove: false, reasoning: "No bullying advantage - Octopus can counter", jukeScore: 0 };
  }

  /**
   * Simulate what happens if the capturing piece becomes zombified
   */
  private simulateZombificationThreats(
    moveInfo: MoveInfo,
    gameState: GameState,
    capturingPiece: IPiece,
    fungiKing: IPiece
  ): {
    canZombieRace: boolean;
    canZombieKingslayer: boolean;
    reasoning: string;
  } {
    try {
      // Clone the game state and simulate the capture
      const testState = cloneFullGameState(gameState);

      // Apply the capture move
      applyMoveWithSnakeRespawn(
        testState,
        moveInfo.fromPosition,
        moveInfo.move,
        capturingPiece
      );

      // The capturing piece is now at the target position but controlled by the enemy
      const zombiePosition = moveInfo.move.position;
      const zombiePiece = getPieceFromPosition(testState, zombiePosition);

      if (!zombiePiece) {
        return {
          canZombieRace: false,
          canZombieKingslayer: false,
          reasoning: "Zombie piece not found after simulation",
        };
      }

      // Temporarily flip the turn to simulate enemy control of the zombie
      const originalTurn = testState.isLightTurn;
      testState.isLightTurn = !originalTurn; // Enemy's turn now

      // Generate all possible moves for the zombified piece
      const moveService = MCTSMoveGenerationService.getInstance();
      const allZombieMoves = this.getMovesForPiece(
        testState,
        zombiePosition,
        zombiePiece
      );

      // Check for race victory
      const canZombieRace = this.canZombieReachVictoryRow(
        allZombieMoves,
        testState,
        !originalTurn // Enemy color
      );

      // Check for kingslayer victory
      const canZombieKingslayer = this.canZombieCaptureOurKing(
        allZombieMoves,
        testState,
        originalTurn // Our color
      );

      // Generate reasoning
      let reasoning = "";
      if (canZombieRace && canZombieKingslayer) {
        reasoning = "DANGER: Zombie can both race and capture our king!";
      } else if (canZombieRace) {
        reasoning = "DANGER: Zombie can reach victory row for race win!";
      } else if (canZombieKingslayer) {
        reasoning = "DANGER: Zombie can capture our king!";
      } else {
        reasoning = "Safe: Zombie cannot win on its turn";
      }

      return {
        canZombieRace,
        canZombieKingslayer,
        reasoning,
      };
    } catch (error) {
      return {
        canZombieRace: false,
        canZombieKingslayer: false,
        reasoning: `Simulation failed: ${error}`,
      };
    }
  }

  /**
   * Get all possible moves for a specific piece at a position
   */
  private getMovesForPiece(
    gameState: GameState,
    position: Position,
    piece: IPiece
  ): MoveInfo[] {
    try {
      const moveService = MCTSMoveGenerationService.getInstance();
      const allMoves = moveService.getAllMoves(gameState);

      // Filter moves that originate from the zombie's position
      return allMoves.filter(
        (moveInfo) =>
          moveInfo.fromPosition.row === position.row &&
          moveInfo.fromPosition.col === position.col
      );
    } catch (error) {
      return [];
    }
  }

  /**
   * Check if zombie can reach victory row for race win
   */
  private canZombieReachVictoryRow(
    zombieMoves: MoveInfo[],
    gameState: GameState,
    enemyIsLight: boolean
  ): boolean {
    const victoryRow = enemyIsLight ? 7 : 0;

    return zombieMoves.some(
      (moveInfo) => moveInfo.move.position.row === victoryRow
    );
  }

  /**
   * Check if zombie can capture our king
   */
  private canZombieCaptureOurKing(
    zombieMoves: MoveInfo[],
    gameState: GameState,
    ourIsLight: boolean
  ): boolean {
    // Find our king position
    const ourKingPosition = findKing(gameState, ourIsLight, false);
    if (!ourKingPosition) return false;

    // Check if any zombie move can capture our king
    return zombieMoves.some((moveInfo) => {
      const isKillMove =
        moveInfo.move.moveType === MoveType.capture ||
        moveInfo.move.moveType === MoveType.trample;

      if (!isKillMove) return false;

      const targetPos = moveInfo.move.position;
      return (
        targetPos.row === ourKingPosition.row &&
        targetPos.col === ourKingPosition.col
      );
    });
  }
}


